import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { useFormatDate } from "@/shared/hooks/useFormatDate";
import { souserService, type SouserRow, type CreateSouserPayload } from "@/features/sending-org/services/souserService";
import { sendingOrgService, type SendingOrgDropdownItem } from "@/features/sending-org/services/sendingOrgService";
import { Button } from "@/shared/components/ui/Button";
import { Input } from "@/shared/components/ui/Input";
import { Modal } from "@/shared/components/ui/Modal";
import { Spinner } from "@/shared/components/ui/Spinner";
import { Toast } from "@/shared/components/feedback/Toast";

const COUNTRY_OPTIONS = [
  { code: "JP", key: "country_jp" },
  { code: "PH", key: "country_ph" },
  { code: "VN", key: "country_vn" },
  { code: "ID", key: "country_id" },
  { code: "MM", key: "country_mm" },
  { code: "KH", key: "country_kh" },
  { code: "TH", key: "country_th" },
  { code: "CN", key: "country_cn" },
  { code: "NP", key: "country_np" },
  { code: "IN", key: "country_in" },
  { code: "BD", key: "country_bd" },
  { code: "LK", key: "country_lk" },
];

// ── Permission modules config ──────────────────────────────────────────────────
// Add new modules here as they are added to the DB (inquiry, return_home, etc.)
const PERMISSION_MODULES = [
  {
    key: "announcements",
    labelKey: "souser.module_announcements",
    readField:  "announcements_read"  as const,
    writeField: "announcements_write" as const,
  },
  // { key: "inquiry",     labelKey: "souser.module_inquiry",     readField: "inquiry_read",     writeField: "inquiry_write"     },
  // { key: "return_home", labelKey: "souser.module_return_home", readField: "return_home_read", writeField: "return_home_write" },
] as const;

type PermField = (typeof PERMISSION_MODULES)[number]["readField"] | (typeof PERMISSION_MODULES)[number]["writeField"];

type PermForm = Record<PermField, boolean>;

const INIT_PERM_FORM: PermForm = {
  announcements_read:  false,
  announcements_write: false,
};

interface FormState {
  email: string;
  first_name: string;
  last_name: string;
  display_name: string;
  country: string;
  sending_org: string;
  position_title: string;
}

const INIT_FORM: FormState = {
  email: "",
  first_name: "",
  last_name: "",
  display_name: "",
  country: "",
  sending_org: "",
  position_title: "",
};

export const SouserSection = () => {
  const { t } = useTranslation();
  const { userData } = useAuth();
  const { fmtDateTime } = useFormatDate();

  const [records, setRecords] = useState<SouserRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [allSendingOrgs, setAllSendingOrgs] = useState<SendingOrgDropdownItem[]>([]);

  // Form modal
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(INIT_FORM);
  const [permForm, setPermForm] = useState<PermForm>(INIT_PERM_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  // Toggle confirm modal
  const [toggleTarget, setToggleTarget] = useState<SouserRow | null>(null);
  const [toggling, setToggling] = useState(false);

  // Delete confirm modal
  const [deleteTarget, setDeleteTarget] = useState<SouserRow | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Reset password modal
  const [resetTarget, setResetTarget] = useState<SouserRow | null>(null);
  const [resetPassword, setResetPassword] = useState("");
  const [resetPasswordConfirm, setResetPasswordConfirm] = useState("");
  const [resetError, setResetError] = useState("");
  const [resetting, setResetting] = useState(false);

  const [toast, setToast] = useState<{ message: string; type: "success" | "error" } | null>(null);
  const showToast = useCallback((message: string, type: "success" | "error") => {
    setToast({ message, type });
  }, []);

  const loadRecords = useCallback(async () => {
    try {
      setLoading(true);
      const [data, orgs] = await Promise.all([
        souserService.getAll(),
        sendingOrgService.getDropdown(),
      ]);
      setRecords(data);
      setAllSendingOrgs(orgs);
    } catch {
      showToast(t("souser.load_error"), "error");
    } finally {
      setLoading(false);
    }
  }, [showToast, t]);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  const filteredSendingOrgs = useMemo(
    () => form.country
      ? allSendingOrgs.filter((o) => o.country_origin === form.country)
      : allSendingOrgs,
    [allSendingOrgs, form.country],
  );

  const myBu = userData?.businessUnit;

  const filtered = useMemo(() => {
    const buRecords = myBu
      ? records.filter((r) => r.bu_access.some((b) => b.business_unit === myBu))
      : records;
    const q = search.trim().toLowerCase();
    if (!q) return buRecords;
    return buRecords.filter(
      (r) =>
        r.email.toLowerCase().includes(q) ||
        r.first_name.toLowerCase().includes(q) ||
        r.last_name.toLowerCase().includes(q) ||
        r.sending_org.toLowerCase().includes(q),
    );
  }, [records, search, myBu]);

  const openCreate = () => {
    setEditingId(null);
    setForm(INIT_FORM);
    setPermForm(INIT_PERM_FORM);
    setFormError("");
    setModalOpen(true);
  };

  const openEdit = (row: SouserRow) => {
    setEditingId(row.id);
    setForm({
      email:          row.email,
      first_name:     row.first_name,
      last_name:      row.last_name,
      display_name:   row.display_name || "",
      country:        row.country,
      sending_org:    row.sending_org,
      position_title: row.position_title || "",
    });
    // Load current permissions for admin's BU into permForm
    const buAccess = myBu ? row.bu_access.find((b) => b.business_unit === myBu) : null;
    setPermForm({
      announcements_read:  !!buAccess?.announcements_read,
      announcements_write: !!buAccess?.announcements_write,
    });
    setFormError("");
    setModalOpen(true);
  };

  const closeModal = () => {
    if (!saving) setModalOpen(false);
  };

  const handleCountryChange = (country: string) => {
    setForm((f) => ({ ...f, country, sending_org: "" }));
  };

  const handleSave = async () => {
    setFormError("");

    if (!form.first_name.trim()) { setFormError(t("souser.validation_first_name")); return; }
    if (!form.last_name.trim())  { setFormError(t("souser.validation_last_name")); return; }
    if (!form.country)           { setFormError(t("souser.validation_country")); return; }

    if (!editingId) {
      if (!form.email.trim())  { setFormError(t("souser.validation_email")); return; }
      if (!form.sending_org)   { setFormError(t("souser.validation_sending_org")); return; }
    }

    setSaving(true);
    try {
      if (editingId) {
        const calls: Promise<unknown>[] = [
          souserService.update(editingId, {
            first_name:     form.first_name.trim(),
            last_name:      form.last_name.trim(),
            display_name:   form.display_name.trim() || null,
            country:        form.country,
            position_title: form.position_title.trim() || null,
          }),
        ];
        if (myBu) calls.push(souserService.updateBuPermissions(editingId, myBu,
          permForm.announcements_read, permForm.announcements_write));
        await Promise.all(calls);
        showToast(t("souser.save_updated"), "success");
      } else {
        const payload: CreateSouserPayload = {
          announcements_read: permForm.announcements_read,
          announcements_write: permForm.announcements_write,
          email:          form.email.trim().toLowerCase(),
          first_name:     form.first_name.trim(),
          last_name:      form.last_name.trim(),
          display_name:   form.display_name.trim() || undefined,
          sending_org:    form.sending_org,
          country:        form.country,
          position_title: form.position_title.trim() || undefined,
        };
        const result = await souserService.create(payload);
        if (result.warnings?.length) window.alert(t("souser.delivery_warning"));
        showToast(t("souser.save_created"), "success");
      }
      setModalOpen(false);
      await loadRecords();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        t("souser.save_error");
      setFormError(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async () => {
    if (!toggleTarget) return;
    setToggling(true);
    try {
      await souserService.toggleActive(toggleTarget.id, !toggleTarget.is_active);
      const name = `${toggleTarget.first_name} ${toggleTarget.last_name}`;
      showToast(
        toggleTarget.is_active
          ? t("souser.toggle_deactivated", { name })
          : t("souser.toggle_activated", { name }),
        "success",
      );
      setToggleTarget(null);
      await loadRecords();
    } catch {
      showToast(t("souser.toggle_error"), "error");
      setToggleTarget(null);
    } finally {
      setToggling(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await souserService.delete(deleteTarget.id);
      showToast(t("souser.delete_success", { name: `${deleteTarget.first_name} ${deleteTarget.last_name}` }), "success");
      setDeleteTarget(null);
      await loadRecords();
    } catch {
      showToast(t("souser.delete_error"), "error");
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  const openResetPassword = (row: SouserRow) => {
    setResetTarget(row);
    setResetPassword("");
    setResetPasswordConfirm("");
    setResetError("");
  };

  const handleResetPassword = async () => {
    setResetError("");
    if (!resetPassword) { setResetError(t("souser.validation_password_required")); return; }
    if (resetPassword.length < 6) { setResetError(t("souser.validation_password_min")); return; }
    if (resetPassword !== resetPasswordConfirm) { setResetError(t("souser.validation_password_mismatch")); return; }
    if (!resetTarget) return;
    setResetting(true);
    try {
      await souserService.resetPassword(resetTarget.id, resetPassword);
      showToast(t("souser.reset_password_success"), "success");
      setResetTarget(null);
    } catch {
      setResetError(t("souser.reset_password_error"));
    } finally {
      setResetting(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {/* Toolbar */}
      <div className="flex items-center justify-between mb-4">
        <p className="text-xs text-gray-400">
          {!loading && `${filtered.length}${filtered.length !== records.length ? ` / ${records.length}` : ""} ${t("souser.count_label")}`}
        </p>
        <Button size="sm" onClick={openCreate}>
          {t("souser.add_button")}
        </Button>
      </div>

      {/* Search */}
      {!loading && records.length > 0 && (
        <div className="mb-4">
          <div className="relative">
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none"
              fill="none" viewBox="0 0 24 24" stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 115 11a6 6 0 0112 0z" />
            </svg>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("common.search_e")}
              className="w-full pl-9 pr-8 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-gray-50"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        {loading ? (
          <div className="py-10"><Spinner /></div>
        ) : records.length === 0 ? (
          <div className="text-center py-12 text-sm text-gray-400">
            <svg className="w-10 h-10 mx-auto mb-3 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            {t("souser.no_records")}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-sm text-gray-400">
            {t("souser.no_search_results")}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                <th className="px-4 py-3">{t("souser.col_name")}</th>
                <th className="px-4 py-3">{t("souser.field_email")}</th>
                <th className="px-4 py-3">{t("souser.field_sending_org")}</th>
                <th className="px-4 py-3">{t("souser.field_country")}</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">{t("souser.col_last_seen")}</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((row) => {
                return (
                  <tr key={row.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">
                        {row.first_name} {row.last_name}
                      </div>
                      {row.display_name && (
                        <div className="text-xs text-gray-400">&ldquo;{row.display_name}&rdquo;</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{row.email}</td>
                    <td className="px-4 py-3 font-mono text-xs text-gray-600">{row.sending_org}</td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-mono bg-blue-50 text-blue-600">
                        {t(`profile.country_${row.country.toLowerCase()}`, { defaultValue: row.country })}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${row.is_active ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                        {row.is_active ? t("settings.status_active") : t("settings.status_inactive")}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">
                      {row.last_seen ? fmtDateTime(row.last_seen) : <span className="text-gray-300">{t("souser.never_seen")}</span>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 justify-end">
                        <Button size="sm" variant="secondary" onClick={() => openEdit(row)}>
                          {t("common.edit")}
                        </Button>
                        <button
                          type="button"
                          onClick={() => openResetPassword(row)}
                          title={t("souser.modal_reset_password")}
                          className="p-1.5 rounded-lg text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                          </svg>
                        </button>
                        <button
                          type="button"
                          onClick={() => setToggleTarget(row)}
                          title={row.is_active ? t("souser.deactivate") : t("souser.activate")}
                          className={`p-1.5 rounded-lg transition-colors ${row.is_active ? "text-green-500 hover:text-red-500 hover:bg-red-50" : "text-gray-400 hover:text-green-500 hover:bg-green-50"}`}
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            {row.is_active ? (
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                            ) : (
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            )}
                          </svg>
                        </button>
                        <button
                          type="button"
                          onClick={() => setDeleteTarget(row)}
                          title={t("souser.delete")}
                          className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Create / Edit Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={closeModal}
        title={editingId ? t("souser.modal_edit") : t("souser.modal_add")}
        headerAction={
          <Button size="sm" onClick={handleSave} loading={saving}>
            {t("common.save")}
          </Button>
        }
      >
        <div className="space-y-4">
          {/* Email — create only */}
          {!editingId && (
            <Input
              label={t("souser.field_email")}
              type="email"
              placeholder={t("souser.field_email_placeholder")}
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            />
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label={t("souser.field_first_name")}
              placeholder={t("souser.field_first_name_placeholder")}
              value={form.first_name}
              onChange={(e) => {
                const first_name = e.target.value;
                setForm((f) => ({
                  ...f,
                  first_name,
                  display_name: `${first_name} ${f.last_name}`.trim(),
                }));
              }}
            />
            <Input
              label={t("souser.field_last_name")}
              placeholder={t("souser.field_last_name_placeholder")}
              value={form.last_name}
              onChange={(e) => {
                const last_name = e.target.value;
                setForm((f) => ({
                  ...f,
                  last_name,
                  display_name: `${f.first_name} ${last_name}`.trim(),
                }));
              }}
            />
          </div>

          <Input
            label={t("souser.field_display_name")}
            placeholder={t("souser.field_display_name_placeholder")}
            value={form.display_name}
            onChange={(e) => setForm((f) => ({ ...f, display_name: e.target.value }))}
          />

          {/* Country */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("souser.field_country")}
            </label>
            {!editingId ? (
              <select
                value={form.country}
                onChange={(e) => handleCountryChange(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
              >
                <option value="">{t("profile.select_country")}</option>
                {COUNTRY_OPTIONS.map((c) => (
                  <option key={c.code} value={c.code}>
                    {t(`profile.${c.key}`)}
                  </option>
                ))}
              </select>
            ) : (
              <div className="flex items-center gap-2 px-3 py-2 text-sm font-mono font-semibold text-gray-700 bg-gray-100 border border-gray-200 rounded-lg select-none">
                {t(`profile.country_${form.country.toLowerCase()}`, { defaultValue: form.country })}
                <span className="ml-auto text-[10px] font-sans font-normal text-gray-400">{t("souser.readonly")}</span>
              </div>
            )}
          </div>

          {/* Sending Org */}
          {!editingId ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("souser.field_sending_org")}
              </label>
              <select
                value={form.sending_org}
                onChange={(e) => setForm((f) => ({ ...f, sending_org: e.target.value }))}
                disabled={!form.country}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white disabled:bg-gray-50 disabled:text-gray-400"
              >
                <option value="">
                  {form.country ? t("souser.field_select_org") : t("profile.select_country")}
                </option>
                {filteredSendingOrgs.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.value}{o.label ? ` — ${o.label}` : ""}
                  </option>
                ))}
              </select>
              {form.country && filteredSendingOrgs.length === 0 && (
                <p className="text-xs text-amber-600 mt-1">
                  No sending orgs found for this country.
                </p>
              )}
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("souser.field_sending_org")}
              </label>
              <div className="flex items-center gap-2 px-3 py-2 text-sm font-mono font-semibold text-gray-700 bg-gray-100 border border-gray-200 rounded-lg select-none">
                {form.sending_org}
                <span className="ml-auto text-[10px] font-sans font-normal text-gray-400">{t("souser.readonly")}</span>
              </div>
            </div>
          )}

          <Input
            label={t("souser.field_position")}
            placeholder={t("souser.field_position_placeholder")}
            value={form.position_title}
            onChange={(e) => setForm((f) => ({ ...f, position_title: e.target.value }))}
          />

          {/* Primary BU note — create only */}
          {!editingId && userData?.businessUnit && (
            <div className="flex items-center gap-2 px-3 py-2 text-xs bg-indigo-50 border border-indigo-100 rounded-lg text-indigo-700">
              <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {t("souser.primary_bu_note", { bu: userData.businessUnit })}
            </div>
          )}

          {/* Bulletin permissions for this business unit */}
          {(
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-sm font-medium text-gray-700">{t("souser.section_permissions")} · {myBu}</span>
                <div className="flex-1 h-px bg-gray-200" />
              </div>
              <div className="rounded-lg border border-gray-200 overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                        {t("souser.perm_module")}
                      </th>
                      <th className="px-3 py-2 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide w-20">
                        {t("souser.perm_read")}
                      </th>
                      <th className="px-3 py-2 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide w-20">
                        {t("souser.perm_write")}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {PERMISSION_MODULES.map((mod) => (
                      <tr key={mod.key} className="hover:bg-gray-50">
                        <td className="px-3 py-2.5 text-sm text-gray-700 font-medium">
                          {t(mod.labelKey, { defaultValue: mod.key })}
                        </td>
                        <td className="px-3 py-2.5 text-center">
                          <input
                            type="checkbox"
                            checked={permForm[mod.readField]}
                            onChange={() =>
                              setPermForm((p) => {
                                const newRead = !p[mod.readField];
                                // Unchecking read also unchecks write
                                return {
                                  ...p,
                                  [mod.readField]: newRead,
                                  [mod.writeField]: newRead ? p[mod.writeField] : false,
                                };
                              })
                            }
                            className="w-4 h-4 rounded accent-green-600 cursor-pointer"
                          />
                        </td>
                        <td className="px-3 py-2.5 text-center">
                          <input
                            type="checkbox"
                            checked={permForm[mod.writeField]}
                            onChange={() =>
                              setPermForm((p) => {
                                const newWrite = !p[mod.writeField];
                                // Checking write also checks read
                                return {
                                  ...p,
                                  [mod.writeField]: newWrite,
                                  [mod.readField]: newWrite ? true : p[mod.readField],
                                };
                              })
                            }
                            className="w-4 h-4 rounded accent-indigo-600 cursor-pointer"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {formError && <p className="text-sm text-red-600">{formError}</p>}
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={!!deleteTarget}
        onClose={() => { if (!deleting) setDeleteTarget(null); }}
        title={t("souser.modal_delete")}
      >
        <div className="space-y-4">
          {deleteTarget && (
            <p className="text-sm font-semibold text-gray-900 bg-gray-50 rounded-lg px-3 py-2">
              {deleteTarget.first_name} {deleteTarget.last_name}
              <span className="text-gray-400 font-normal ml-2">{deleteTarget.email}</span>
            </p>
          )}
          <p className="text-sm text-gray-600">{t("souser.delete_warning")}</p>
          <div className="flex gap-3 justify-end pt-1">
            <Button variant="secondary" onClick={() => setDeleteTarget(null)} disabled={deleting}>
              {t("common.cancel")}
            </Button>
            <Button variant="danger" onClick={handleDelete} loading={deleting}>
              {t("souser.delete")}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Reset Password Modal */}
      <Modal
        isOpen={!!resetTarget}
        onClose={() => { if (!resetting) setResetTarget(null); }}
        title={t("souser.modal_reset_password")}
        headerAction={
          <Button size="sm" onClick={handleResetPassword} loading={resetting}>
            {t("common.save")}
          </Button>
        }
      >
        <div className="space-y-4">
          {resetTarget && (
            <p className="text-sm font-semibold text-gray-900 bg-gray-50 rounded-lg px-3 py-2">
              {resetTarget.first_name} {resetTarget.last_name}
              <span className="text-gray-400 font-normal ml-2">{resetTarget.email}</span>
            </p>
          )}
          <Input
            label={t("souser.reset_password_label")}
            type="password"
            placeholder={t("souser.reset_password_placeholder")}
            value={resetPassword}
            onChange={(e) => setResetPassword(e.target.value)}
          />
          <Input
            label={t("souser.reset_password_confirm_label")}
            type="password"
            placeholder={t("souser.reset_password_confirm_placeholder")}
            value={resetPasswordConfirm}
            onChange={(e) => setResetPasswordConfirm(e.target.value)}
          />
          {resetError && <p className="text-sm text-red-600">{resetError}</p>}
        </div>
      </Modal>

      {/* Toggle Active Confirmation Modal */}
      <Modal
        isOpen={!!toggleTarget}
        onClose={() => { if (!toggling) setToggleTarget(null); }}
        title={toggleTarget?.is_active ? t("souser.modal_deactivate") : t("souser.modal_activate")}
      >
        <div className="space-y-4">
          {toggleTarget && (
            <p className="text-sm font-semibold text-gray-900 bg-gray-50 rounded-lg px-3 py-2">
              {toggleTarget.first_name} {toggleTarget.last_name}
              <span className="text-gray-400 font-normal ml-2">{toggleTarget.email}</span>
            </p>
          )}
          <p className="text-sm text-gray-600">
            {toggleTarget?.is_active ? t("souser.deactivate_warning") : t("souser.activate_warning")}
          </p>
          <div className="flex gap-3 justify-end pt-1">
            <Button variant="secondary" onClick={() => setToggleTarget(null)} disabled={toggling}>
              {t("common.cancel")}
            </Button>
            <Button
              variant={toggleTarget?.is_active ? "danger" : "primary"}
              onClick={handleToggle}
              loading={toggling}
            >
              {toggleTarget?.is_active ? t("souser.deactivate") : t("souser.activate")}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
